import React, { useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import { DragIndicator } from "@material-ui/icons";
import { useDrag, useDrop } from "react-dnd";
import SettingsIcon from "@material-ui/icons/Settings";
import CustomDatePicker from "@jumbo/Dashboards/Gallery/components/datePicker";
import { dateFormat } from "@jumbo/utils/shamsiStandardFormat";

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: "100%",
    minWidth: 0,
    flex: "1 1 0%",
  },
  checkFieldsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    opacity: (props) => (props.isDragging ? 0.5 : 1),
    minWidth: 0,
    gap: theme.spacing(0.5),
    '& > svg, & > .MuiBox-root': { flexShrink: 0 },
  },
  textFieldLabel: {
    color: theme.palette.primary.main,
    marginBottom: "16px",
    fontWeight: "bold",
  },
}));

const FieldOption = ({
  item,
  handleChangeOptionField,
  handleDeleteOptionField,
  handleChangeSelectionList,
  index,
  moveItem,
}) => {
  const classes = useStyles();
  const ref = React.useRef(null);
  const ItemType = "FIELD_OPTION";

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [, drop] = useDrop({
    accept: ItemType,
    hover(draggedItem, monitor) {
      if (!ref.current) {
        return;
      }

      const hoverIndex = index;
      const dragIndex = draggedItem.index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveItem(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { type: ItemType, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const open = Boolean(anchorEl);
  const id = open ? "limit-popover" : undefined;

  return (
    <Box
      className={classes.checkFieldsContainer}
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <DragIndicator />
      <TextField
        id={`option-${index}`}
        label={"گزینه"}
        variant="outlined"
        className={classes.formControl}
        value={item.title}
        onChange={(e) =>
          handleChangeOptionField(e.target.value, item._id || item.id)
        }
      />
      <Box
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <IconButton
          aria-label="delete"
          size="small"
          onClick={() => handleDeleteOptionField(item._id || item.id)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>

        <IconButton aria-label="settings" size="small" onClick={handleClick}>
          <SettingsIcon fontSize="small" />
        </IconButton>
        <Popover
          style={{ overflowX: "visible" }}
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box padding={"16px"}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={item?.limits?.count}
                  onChange={(e) => {
                    const changedItem = {
                      ...item,
                      limits: { ...item.limits, count: e.target.checked },
                    };
                    handleChangeSelectionList(changedItem);
                  }}
                  name={"CountLimit"}
                />
              }
              label={"تعداد محدود"}
            />
            {item?.limits?.count && (
              <>
                <Typography className={classes.textFieldLabel}>
                  تعداد
                </Typography>
                <TextField
                  id="limit"
                  label="تعداد"
                  variant="outlined"
                  type="number"
                  className={classes.formControl}
                  value={item.limits.countLimit}
                  onChange={(e) => {
                    const changedItem = {
                      ...item,
                      limits: { ...item.limits, countLimit: e.target.value },
                    };
                    handleChangeSelectionList(changedItem);
                  }}
                  onWheel={(e) => e.target.blur()}
                />
              </>
            )}

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={item?.limits?.date}
                    onChange={(e) => {
                      const changedItem = {
                        ...item,
                        limits: { ...item.limits, date: e.target.checked },
                      };
                      handleChangeSelectionList(changedItem);
                    }}
                    name={"DateLimit"}
                  />
                }
                label={"زمان محدود"}
              />

              {item?.limits?.date && (
                <>
                  <CustomDatePicker
                    title={"زمان آغاز"}
                    value={item?.limits?.startDate || dateFormat}
                    updateWithVal={(val) => {
                      const changedItem = {
                        ...item,
                        limits: { ...item.limits, startDate: val },
                      };
                      handleChangeSelectionList(changedItem);
                    }}
                  />
                  <CustomDatePicker
                    title={"زمان پایان"}
                    value={item?.limits?.endDate || dateFormat}
                    updateWithVal={(val) => {
                      const changedItem = {
                        ...item,
                        limits: { ...item.limits, endDate: val },
                      };
                      handleChangeSelectionList(changedItem);
                    }}
                  />
                </>
              )}
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={item?.markAsAttended}
                    onChange={(e) => {
                      const changedItem = {
                        ...item,
                        markAsAttended: e.target.checked,
                      };
                      handleChangeSelectionList(changedItem);
                    }}
                    name={"MarkAsAttended"}
                  />
                }
                label={"تحویل داده شده"}
              />
            </Box>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default FieldOption;
